import React from "react";
import { Box, Typography, useTheme } from "@mui/material";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import EarningsSessionColumn from "./EarningsSessionColumn";
import {
  FOOTER_HEIGHT,
  ROW_GAP,
  SESSION_BODY_HEIGHT,
  SESSION_HEADER_HEIGHT,
  VisibleDay,
  footerLabel,
} from "./earningsCalendarSelection";
import { touchHitAreaSx } from "./touchHitArea";
import { focusRingSx, panelBorder, panelHairline } from "../overviewPanel";
import { hapticSelection } from "../../../lib/haptics";
import { useTouchLayout } from "../../../hooks/useTouchLayout";

dayjs.extend(utc);

// One day of the week grid: a date header, the session pair side by side at
// equal width, then the day's footer. Every piece has a fixed height, so every
// block on the board is the same shape whether it holds forty companies or
// none — the eye can run along the week without the columns jumping.
//
// The reporting date is a calendar date, not an instant: parsing its
// midnight-UTC form in a western timezone would roll it back a day, so it stays
// on the UTC clock the server grouped it on.

interface EarningsDayColumnProps {
  day: VisibleDay;
  isToday: boolean;
  /** What a day with nothing in it says — depends on whether a size filter is
   *  hiding the entries or there genuinely are none. */
  emptyLabel: string;
  onOpenDay: (date: string) => void;
}

const EarningsDayColumn: React.FC<EarningsDayColumnProps> = ({
  day,
  isToday,
  emptyLabel,
  onOpenDay,
}) => {
  const theme = useTheme();
  const isTouch = useTouchLayout();
  const isDark = theme.palette.mode === "dark";
  const date = dayjs.utc(day.date);
  const isEmpty = day.totalCount === 0;
  const label = footerLabel(day);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
      <Box
        sx={{
          mb: 1.25,
          px: 0.25,
          pb: 0.75,
          borderBottom: `1px solid ${panelHairline(isDark)}`,
          ...(isToday && { borderBottomColor: theme.palette.success.main }),
        }}
      >
        <Box sx={{ display: "flex", alignItems: "baseline", gap: 1 }}>
          <Typography
            sx={{
              minWidth: 0,
              fontSize: "0.9375rem",
              fontWeight: 600,
              lineHeight: 1.2,
              letterSpacing: "-0.01em",
              color: isToday ? theme.palette.success.main : theme.palette.text.primary,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {date.format("dddd")}
          </Typography>
          {isToday && (
            <Box
              role="img"
              aria-label="Today"
              title="Today"
              sx={{
                width: 6,
                height: 6,
                borderRadius: "50%",
                backgroundColor: theme.palette.success.main,
                flexShrink: 0,
                alignSelf: "center",
                animation: "earningsTodayPulse 2.4s ease-in-out infinite",
                "@keyframes earningsTodayPulse": {
                  "0%, 100%": { opacity: 1 },
                  "50%": { opacity: 0.35 },
                },
                "@media (prefers-reduced-motion: reduce)": {
                  animation: "none",
                },
              }}
            />
          )}
          <Box sx={{ flex: 1 }} />
          <Typography
            sx={{
              fontSize: "0.6875rem",
              fontWeight: 700,
              color: theme.palette.text.disabled,
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {day.totalCount}
          </Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.75,
            mt: 0.25,
            height: 16,
          }}
        >
          <Typography
            sx={{
              fontSize: "0.75rem",
              color: theme.palette.text.secondary,
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {date.format("D MMM")}
          </Typography>
        </Box>
      </Box>

      {/* The pair. Equal width by construction and a fixed height by
          declaration, so the two sides read as one instrument. */}
      {!isEmpty && (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
            gap: 1,
          }}
        >
          <EarningsSessionColumn session={day.bmo} fixedHeight />
          <EarningsSessionColumn session={day.amc} fixedHeight />
        </Box>
      )}

      {/* A quiet day takes exactly the space the pair would, and says so. */}
      {isEmpty && (
        <Box
          sx={{
            height: SESSION_HEADER_HEIGHT + ROW_GAP + SESSION_BODY_HEIGHT,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: 1.25,
            border: `1px solid ${panelHairline(isDark)}`,
          }}
        >
          <Typography
            sx={{ fontSize: "0.75rem", color: theme.palette.text.disabled }}
          >
            {emptyLabel}
          </Typography>
        </Box>
      )}

      {/* The day's door, not a session's: it spans the pair because the rows it
          hides come from both sides. Always present so the block keeps its
          height; always opens the day while there is one to open. */}
      <Box sx={{ mt: 1.25, height: FOOTER_HEIGHT, flexShrink: 0 }}>
        {label && (
          <Typography
            component="button"
            onClick={() => {
              hapticSelection();
              onOpenDay(day.date);
            }}
            aria-label={`Show all ${day.totalCount} companies reporting on ${date.format("dddd D MMMM")}`}
            sx={{
              all: "unset",
              ...focusRingSx,
              ...(isTouch && touchHitAreaSx(FOOTER_HEIGHT)),
              boxSizing: "border-box",
              cursor: "pointer",
              fontFamily: "inherit",
              width: "100%",
              height: FOOTER_HEIGHT,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: 1.25,
              border: `1px dashed ${panelBorder(isDark)}`,
              fontSize: "0.6875rem",
              fontWeight: 600,
              color: theme.palette.text.secondary,
              WebkitTapHighlightColor: "transparent",
              transition:
                "color 0.2s ease, border-color 0.2s ease, background-color 0.2s ease",
              "&:hover": {
                color: "#22c55e",
                borderColor: "rgba(34, 197, 94, 0.45)",
                backgroundColor: isDark
                  ? "rgba(34,197,94,0.05)"
                  : "rgba(34,197,94,0.04)",
              },
            }}
          >
            {label}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default React.memo(EarningsDayColumn);
